import {Shift} from '@domain/shifthandover'
import {useOnPageWithoutUnmountFocus} from '@hconnect/common/hooks'
import {useEndOfPageScrollDebounce} from '@hconnect/common/hooks/useListScrollDebounce'
import {ShiftNotFoundError, SortOrder} from '@hconnect/common/types'
import {getQuickSelectFilter} from '@hconnect/common/utils'
import {PaginationOptions} from '@hconnect/uikit'
import {useUpmPlantNow} from '@hconnect/upm'
import {Theme, useMediaQuery} from '@mui/material'
import {
  AllFilterOptions,
  CombinedWorkOrdersFilterOptions,
  FilterOptions,
  Page,
  PaginationParams,
  WorkOrdersPayload,
  WorkOrderSortBy
} from '@widgets/shift-event-filters'
import {useEffect, useState} from 'react'

import {useShiftEventsPageFilters} from './useShiftEventsFilter'

export const MOBILE_ELEMENTS_PER_PAGE = 50

type UsePagedDataProps<T> = {
  plantId: string
  shifts: Shift[]
  fetchData: (
    plantId: string,
    shifts: Shift[],
    params: WorkOrdersPayload<CombinedWorkOrdersFilterOptions>
  ) => {
    data: PaginationParams<T> | undefined
    isLoading: boolean
    isFetching: boolean
    refetch: () => void
  }
  pageType: Page.WorkOrders | Page.Notifications
}

type PagedDataResult<T> = {
  data: PaginationParams<T> | undefined
  isLoading: boolean
  filterOptions: FilterOptions
  setFilters: (filters: FilterOptions) => void
  changePage: (pageNumber: number) => void
  setItemsPerPage: (itemsPerPage: number) => void
  paginationOptions: PaginationOptions
  shouldLoadNextPage: boolean
}

export function usePagedData<T>({
  plantId,
  shifts,
  fetchData,
  pageType
}: UsePagedDataProps<T>): PagedDataResult<T> {
  const getPlantNow = useUpmPlantNow(plantId)

  const [pageNumber, changePage] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(50)
  const [filterOptions, setFilters] = useShiftEventsPageFilters(
    plantId,
    pageType,
    pageType === Page.WorkOrders
      ? {
          sortWorkOrderBy: 'status' as WorkOrderSortBy,
          woSortDir: SortOrder.Descending
        }
      : undefined
  )

  const extendedFilterOptions: AllFilterOptions = {...filterOptions}
  if (!extendedFilterOptions.timeRange) {
    const [startDate, endDate] = getQuickSelectFilter('currentShift', shifts, getPlantNow())

    if (!startDate || !endDate) {
      throw new ShiftNotFoundError()
    }

    extendedFilterOptions.timeRange = {
      startDate: startDate,
      endDate: endDate
    }
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const {data, isLoading, isFetching, refetch} = fetchData(plantId, shifts, {
    input: extendedFilterOptions,
    offset: pageNumber * itemsPerPage,
    limit: itemsPerPage
  })

  useOnPageWithoutUnmountFocus(refetch)

  const shouldLoadNextPage = itemsPerPage < (data?.total || 0)

  const {trackScrolling} = useEndOfPageScrollDebounce(() => {
    setItemsPerPage((items) => items + MOBILE_ELEMENTS_PER_PAGE)
  }, shouldLoadNextPage)

  useEffect(() => {
    if (isMobile) {
      document.addEventListener('scroll', trackScrolling)
    }

    return () => document.removeEventListener('scroll', trackScrolling)
  }, [isMobile, trackScrolling])

  useEffect(() => {
    if (isMobile) {
      setItemsPerPage(MOBILE_ELEMENTS_PER_PAGE)
      changePage(0)
    }
  }, [isMobile])

  const onPaginationChange = (pageNumber: number, itemsPerPage: number) => {
    changePage(pageNumber)
    setItemsPerPage(itemsPerPage)
  }

  return {
    data,
    isLoading: isLoading || isFetching,
    filterOptions,
    setFilters,
    changePage,
    setItemsPerPage,
    paginationOptions: {
      page: pageNumber,
      rowsPerPage: itemsPerPage,
      onPaginationChange,
      totalDataLength: data?.total ?? 0
    },
    shouldLoadNextPage
  }
}
